import React, { useState, useEffect, useCallback, useRef } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { depositsApi } from "../../services/depositsApi";
import { CreatePaymentIntentRequest } from "../../types/payment";
import {
  CurrencyEuroIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/api";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useAuth } from "../../contexts/AuthContext";
import { formatSecondaryNumber } from "../../utils/currency";

interface VatCalculation {
  netAmount: number;
  vatPercent: number;
  vatAmount: number;
  grossAmount: number;
  specialText: string | null;
  scenario: string;
  explanation: string;
}

interface VatCalculationResponse {
  success: boolean;
  data: VatCalculation;
  message: string;
}

interface PaymentFormProps {
  amount: number;
  onSuccess?: (depositId: string) => void;
  onError?: (error: string) => void;
}

const USE_STRIPE_CHECKOUT = import.meta.env.VITE_STRIPE_USE_CHECKOUT === "true";

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { dual, rate } = useCurrency();
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [depositId, setDepositId] = useState<string>("");
  const [vatId, setVatId] = useState("");
  const [isValidatingVat, setIsValidatingVat] = useState(false);
  const [vatValidationStatus, setVatValidationStatus] = useState<
    "idle" | "valid" | "invalid"
  >("idle");
  const [vatCalculation, setVatCalculation] = useState<VatCalculation | null>(
    null,
  );
  const [isCalculatingVat, setIsCalculatingVat] = useState(false);

  // Throttling refs to prevent excessive API calls
  const lastVatCalculationRef = useRef<number>(0);
  const lastCalculatedAmountRef = useRef<number | null>(null);
  const lastCalculatedVatStatusRef = useRef<"idle" | "valid" | "invalid">(
    "idle",
  );
  const isCalculatingRef = useRef<boolean>(false);
  const hasPrefilledVatIdRef = useRef<boolean>(false); // Track if VAT ID has been prefilled
  const isValidatingRef = useRef<boolean>(false); // Track if validation is in progress
  const MIN_VAT_CALCULATION_INTERVAL = 2000; // Minimum 2 seconds between VAT calculations

  // Create payment intent mutation
  const createPaymentIntentMutation = useMutation({
    mutationFn: (data: CreatePaymentIntentRequest) => {
      return depositsApi.createPaymentIntent(data);
    },
    onSuccess: (response) => {
      const payload: any = (response as any)?.data ?? response;

      if (!payload?.clientSecret || !payload?.depositId) {
        console.warn(
          "Unexpected response shape for create-intent, falling back to envelope.data",
        );
      }
      setClientSecret(payload?.clientSecret || "");
      setDepositId(payload?.depositId || "");
    },
    onError: (error: any) => {
      console.error("Payment intent creation failed:");
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to create payment intent";
      toast.error(message);
      onError?.(message);
      navigate("/payment-failed", { state: { reason: message } });
    },
  });

  // Calculate VAT with throttling and debouncing
  const calculateVat = useCallback(
    async (explicitVatValidationStatus?: "idle" | "valid" | "invalid") => {
      const currentValidationStatus =
        explicitVatValidationStatus !== undefined
          ? explicitVatValidationStatus
          : vatValidationStatus;
      if (amount <= 0) {
        setVatCalculation(null);
        lastCalculatedAmountRef.current = null;
        return;
      }

      // Prevent multiple simultaneous calculations
      if (isCalculatingRef.current) {
        return;
      }

      // Skip if same amount and VAT status were just calculated (unless explicitly called)
      if (explicitVatValidationStatus === undefined) {
        const shouldSkip =
          lastCalculatedAmountRef.current === amount &&
          lastCalculatedVatStatusRef.current === currentValidationStatus;

        if (shouldSkip) {
          const now = Date.now();
          const timeSinceLastCalculation = now - lastVatCalculationRef.current;
          if (timeSinceLastCalculation < MIN_VAT_CALCULATION_INTERVAL) {
            return;
          }
        }

        // Throttle: Don't calculate if last calculation was too recent
        const now = Date.now();
        const timeSinceLastCalculation = now - lastVatCalculationRef.current;
        if (timeSinceLastCalculation < MIN_VAT_CALCULATION_INTERVAL) {
          return;
        }
      }

      const now = Date.now();
      isCalculatingRef.current = true;
      setIsCalculatingVat(true);
      lastVatCalculationRef.current = now;
      lastCalculatedAmountRef.current = amount;
      lastCalculatedVatStatusRef.current = currentValidationStatus;

      try {
        // Send a validated VAT ID, an explicit null for invalid input, or
        // undefined to use the dealer's saved VAT ID.
        const vatIdToSend =
          currentValidationStatus === "valid"
            ? vatId
            : currentValidationStatus === "invalid"
              ? null
              : undefined;

        const response = await apiService.post<VatCalculationResponse>(
          "/dealer-receipts/calculate-vat-preview",
          {
            netAmount: amount,
            vatId: vatIdToSend,
          },
        );

        // Backend returns { success, data, message } where data contains VatCalculation
        const vatData = (response.data as any).data || response.data;
        setVatCalculation(vatData);
      } catch (error: any) {
        console.error("VAT calculation failed.");

        // Preserve the existing German VAT fallback while the service is unavailable.
        const fallbackVatPercent = 19;
        const fallbackVatAmount =
          Math.round(amount * (fallbackVatPercent / 100) * 100) / 100;
        const fallbackGrossAmount = amount + fallbackVatAmount;

        setVatCalculation({
          netAmount: amount,
          vatPercent: fallbackVatPercent,
          vatAmount: fallbackVatAmount,
          grossAmount: fallbackGrossAmount,
          specialText: null,
          scenario: "germany",
          explanation: "Fallback calculation (VAT service unavailable)",
        });

        toast.error("Could not calculate VAT from server, using default rate");
      } finally {
        isCalculatingRef.current = false;
        setIsCalculatingVat(false);
      }
    },
    [amount, vatId, vatValidationStatus],
  );

  // Initialize VAT ID from user profile if dealer has a registered VAT ID
  // Prefill VAT ID if it exists in profile (even if vatValidity is false)
  // If VAT ID input is filled (from any source), automatically validate it on mount
  useEffect(() => {
    // Only run once on mount
    if (hasPrefilledVatIdRef.current) {
      return;
    }

    hasPrefilledVatIdRef.current = true; // Mark as processed

    // Prefill VAT ID from user profile if it exists
    if (user?.vatId) {
      setVatId(user.vatId);
    }

    // Capture user VAT ID value for use in setTimeout (avoid closure issues)
    const userVatId = user?.vatId;

    // Wait a bit for state to update, then check if VAT ID input is filled
    setTimeout(async () => {
      // Check if VAT ID exists (from user profile)
      if (userVatId && userVatId.trim() !== "") {
        isValidatingRef.current = true;
        setIsValidatingVat(true);
        setVatValidationStatus("idle");

        try {
          await apiService.post("/dealer-receipts/validate-vat", {
            vatId: userVatId,
          });
          toast.success("VAT ID is valid");
          setVatValidationStatus("valid");
          lastCalculatedVatStatusRef.current = "idle"; // Reset to force recalculation
          await calculateVat("valid");
        } catch (error: any) {
          console.error("VAT ID validation failed.");

          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Invalid VAT ID";

          // Check if this is a test mode error
          if (
            errorMessage.includes("test mode") ||
            errorMessage.includes("not possible in the test mode")
          ) {
            toast.error(
              "VAT validation is currently unavailable. The system is in test mode.",
            );
            setVatValidationStatus("idle");
            lastCalculatedVatStatusRef.current = "idle";
            await calculateVat("idle");
          } else {
            setVatValidationStatus("invalid");
            lastCalculatedVatStatusRef.current = "idle"; // Reset to force recalculation
            await calculateVat("invalid");
          }
        } finally {
          setIsValidatingVat(false);
          isValidatingRef.current = false;
        }
      } else {
        // No VAT ID - calculate VAT without VAT ID (will apply 19% for EU countries)

        setTimeout(() => calculateVat("idle"), 100);
      }
    }, 200);

    // Log user country for debugging

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user, calculateVat is stable and only used in setTimeout

  // Calculate VAT when amount changes
  useEffect(() => {
    // Only calculate if amount actually changed
    if (lastCalculatedAmountRef.current !== amount) {
      calculateVat();
    }
  }, [amount, calculateVat]);

  // Calculate VAT when VAT validation status changes
  useEffect(() => {
    // Skip if validateVatId is currently running (it will call calculateVat directly)
    if (isValidatingRef.current) {
      return;
    }

    // Only calculate if VAT status actually changed
    if (lastCalculatedVatStatusRef.current !== vatValidationStatus) {
      calculateVat();
    }
  }, [vatValidationStatus, calculateVat]);

  const validateVatId = async () => {
    const trimmedVatId = vatId?.trim() || "";

    if (!trimmedVatId) {
      setVatValidationStatus("idle");
      lastCalculatedVatStatusRef.current = "idle";
      calculateVat("idle");
      toast.error("Please enter a VAT ID");
      return;
    }

    isValidatingRef.current = true;
    setIsValidatingVat(true);
    setVatValidationStatus("idle");

    try {
      await apiService.post("/dealer-receipts/validate-vat", {
        vatId: trimmedVatId,
      });

      setVatValidationStatus("valid");
      lastCalculatedVatStatusRef.current = "idle"; // Reset to force recalculation
      await calculateVat("valid");
      toast.success("VAT ID is valid");
    } catch (error: any) {
      console.error("VAT ID validation failed.");

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Invalid VAT ID";

      // Check if this is a test mode error
      if (
        errorMessage.includes("test mode") ||
        errorMessage.includes("not possible in the test mode")
      ) {
        toast.error(
          "VAT validation is currently unavailable. The system is in test mode.",
        );
        setVatValidationStatus("idle");
        lastCalculatedVatStatusRef.current = "idle";
        await calculateVat("idle");
      } else {
        setVatValidationStatus("invalid");
        lastCalculatedVatStatusRef.current = "idle"; // Reset to force recalculation
        await calculateVat("invalid");
        // Don't show toast - error is already displayed in UI below VAT ID input
      }
    } finally {
      setIsValidatingVat(false);
      isValidatingRef.current = false;
    }
  };

  // Payment intent should be created when user clicks "Pay" button
  // This ensures the latest VAT calculation (including VAT ID) is used

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (USE_STRIPE_CHECKOUT) {
      if (!vatCalculation) {
        toast.error("VAT calculation not ready. Please wait...");
        return;
      }

      setIsProcessing(true);
      try {
        const vatIdToSend =
          vatValidationStatus === "valid"
            ? vatId
            : vatValidationStatus === "invalid"
              ? null
              : undefined;
        const response: any = await apiService.post(
          "/deposits/create-checkout",
          {
            amount: vatCalculation.grossAmount,
            currency: "EUR",
            metadata: {
              netAmount: vatCalculation.netAmount,
              vatId: vatIdToSend,
              vatPercent: vatCalculation.vatPercent,
            },
          },
        );
        const url = response?.data?.checkoutUrl || response?.checkoutUrl;
        if (!url) throw new Error("Checkout URL not received from server");
        window.location.href = url;
        return;
      } catch (error: any) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to create checkout session";
        toast.error(message);
        onError?.(message);
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    if (!stripe) {
      toast.error(t("toast.stripeNotLoaded"));
      console.error("Stripe is not loaded");
      return;
    }

    if (!elements) {
      toast.error(t("toast.stripeElementsNotReady"));
      console.error("Elements not ready");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error(t("toast.cardElementNotFound"));
      return;
    }

    setIsProcessing(true);

    try {
      // Create payment intent if not already created
      // This ensures we use the latest VAT calculation (including VAT ID)
      let finalClientSecret = clientSecret;
      let finalDepositId = depositId;

      if (!finalClientSecret) {
        if (!vatCalculation) {
          toast.error("VAT calculation not ready. Please wait...");
          setIsProcessing(false);
          return;
        }

        const paymentIntentData = {
          amount: vatCalculation.grossAmount,
          currency: "EUR",
          metadata: {
            netAmount: vatCalculation.netAmount,
            // Send VAT ID based on validation status:
            // - 'valid': send the validated VAT ID
            // - 'invalid': send null (explicitly invalid, don't show in invoice)
            // - 'idle': send undefined (use dealer's saved VAT ID from database)
            vatId:
              vatValidationStatus === "valid"
                ? vatId
                : vatValidationStatus === "invalid"
                  ? null
                  : undefined,
            vatPercent: vatCalculation.vatPercent,
          },
        };

        const response: any =
          await depositsApi.createPaymentIntent(paymentIntentData);

        const payload = response?.data ?? response;
        finalClientSecret = payload?.clientSecret;
        finalDepositId = payload?.depositId;

        if (!finalClientSecret || !finalDepositId) {
          throw new Error("Invalid response from payment intent creation");
        }

        setClientSecret(finalClientSecret);
        setDepositId(finalDepositId);
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        finalClientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        },
      );

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === "succeeded") {
        // Confirm deposit with backend to save card info and update status
        try {
          const confirmResult = await depositsApi.confirmDeposit(
            finalDepositId,
            paymentIntent.id,
          );

          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ["epoint-balance"] });
          queryClient.invalidateQueries({ queryKey: ["transactions"] });
          queryClient.invalidateQueries({ queryKey: ["recent-transactions"] });
          queryClient.invalidateQueries({ queryKey: ["monthly-stats"] });
          queryClient.invalidateQueries({ queryKey: ["deposit-history"] });

          // Force refresh balance immediately
          setTimeout(() => {
            queryClient.refetchQueries({ queryKey: ["epoint-balance"] });
          }, 1000);

          toast.success(t("toast.paymentSuccess"));
          onSuccess?.(finalDepositId);
        } catch (confirmError: any) {
          console.error("Deposit confirmation failed after payment.");

          // If confirmation fails, the payment succeeded but ePoints weren't added
          // This is a critical error that needs user attention
          const errorMessage =
            confirmError.response?.data?.message ||
            confirmError.message ||
            "Failed to confirm deposit";
          toast.error(
            t("toast.paymentSuccessConfirmFailed", { error: errorMessage }),
          );
          onError?.(`Confirmation failed: ${errorMessage}`);
        }
      } else {
        throw new Error("Payment was not successful");
      }
    } catch (error: any) {
      const message = error.message || "Payment failed";
      toast.error(message);
      onError?.(message);
      // Stay on page to allow retry without refilling inputs
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    hidePostalCode: true, // Let backoffice/Stripe validate server-side; avoid ZIP UI/validation
    style: {
      base: {
        fontSize: "16px",
        lineHeight: "24px", // Prevents overlapping on small screens (iOS Safari)
        color: "#6b7280", // Gray-500 for better visibility
        "::placeholder": {
          color: "#9ca3af", // Gray-400 for placeholder
        },
      },
      invalid: {
        color: "#dc2626", // Red-600 for errors
      },
    },
  } as const;

  const epointsAmount = amount * 100; // 1 EUR = 100 ePoints

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <CreditCardIcon className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-secondary">
              {t("payment.completePayment")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("payment.securePayment")}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="relative bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          {t("payment.paymentOverview")}
        </h4>

        {isValidatingVat || isCalculatingVat ? (
          /* Skeleton Loading State */
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
            </div>
            <div className="border-t border-gray-300 my-2"></div>
            <div className="flex items-center justify-between">
              <div className="h-5 bg-gray-200 rounded w-40 animate-pulse"></div>
              <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-4 bg-gray-200 rounded w-36 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
            </div>
          </div>
        ) : (
          /* Actual Content */
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">
                {t("payment.netAmount")}
              </span>
              <span className="text-secondary">
                €{dual(vatCalculation?.netAmount ?? amount)}
              </span>
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">
                {t("payment.vatWithPercent", {
                  percent: vatCalculation?.vatPercent ?? 0,
                })}
              </span>
              <span className="text-secondary">
                €{dual(vatCalculation?.vatAmount ?? 0)}
              </span>
            </div>

            <div className="border-t border-gray-300 my-2"></div>

            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-semibold text-gray-700">
                {t("payment.grossAmount")}
              </span>
              <div className="text-right">
                <div className="text-lg font-bold text-primary-600">
                  €{(vatCalculation?.grossAmount ?? amount).toFixed(2)}
                </div>
                {rate && (
                  <div className="text-xs text-gray-400">
                    ≈{" "}
                    {formatSecondaryNumber(
                      vatCalculation?.grossAmount ?? amount,
                      rate,
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {t("payment.youWillReceive")}
              </span>
              <span className="font-semibold text-primary-600">
                +{epointsAmount.toLocaleString()} ePoints
              </span>
            </div>
          </>
        )}
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* VAT ID Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("payment.vatIdOptional")}
          </label>
          <div className="flex space-x-2">
            <div className="flex-1">
              <input
                type="text"
                value={vatId}
                onChange={(e) => setVatId(e.target.value.toUpperCase())}
                className="w-full border border-gray-300 rounded-md p-3 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={validateVatId}
              disabled={!vatId || isValidatingVat}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidatingVat ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
              ) : (
                t("common.validate")
              )}
            </button>
          </div>

          {/* Validation Status */}
          {vatValidationStatus === "valid" && (
            <div className="mt-2 flex items-center space-x-2 text-green-600">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="text-sm">{t("payment.vatIdValid")}</span>
            </div>
          )}
          {vatValidationStatus === "invalid" && (
            <div className="mt-2 flex items-center space-x-2 text-red-600">
              <XCircleIcon className="h-5 w-5" />
              <span className="text-sm">
                {t("payment.vatIdInvalidOrRegistered")}
              </span>
            </div>
          )}
        </div>

        {/* Card Element - only shown in legacy flow */}
        {!USE_STRIPE_CHECKOUT && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("payment.cardInformation")}
            </label>
            <div className="border border-gray-300 rounded-md p-4 bg-white">
              <CardElement options={cardElementOptions} />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={
            (USE_STRIPE_CHECKOUT ? false : !stripe) ||
            isProcessing ||
            !vatCalculation ||
            isValidatingVat ||
            isCalculatingVat
          }
          className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing || isValidatingVat || isCalculatingVat ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isProcessing ? t("payment.processingPayment") : ""}
            </div>
          ) : (
            <>
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Pay €{dual(vatCalculation?.grossAmount ?? amount)}
            </>
          )}
        </button>
      </form>

      {/* Security Notice */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>{t("payment.securityNotice")}</p>
        <p>{t("payment.poweredByStripe")}</p>
      </div>
    </div>
  );
};

export default PaymentForm;
